import type { Theme } from '@mui/material/styles';

const getMuiChip = () => ({
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      '&:active': {
        boxShadow: 'none', // Remove shadow on active state
      },
      '&.MuiChip-colorPrimary': {
        backgroundColor: theme.palette.primary.dark,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.primary.dark,
          fill: theme.palette.semantic.background.default,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.semantic.background.default}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.semantic.background.default}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },
      '&.MuiChip-colorSecondary': {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.semantic.text.body,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.secondary.light,
          fill: theme.palette.primary.dark,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },
      '&.MuiChip-colorError': {
        backgroundColor: theme.palette.semantic.background.critical,
        color: theme.palette.semantic.text.critical,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.semantic.background.critical,
          fill: theme.palette.primary.dark,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },
      '&.MuiChip-colorWarning': {
        backgroundColor: theme.palette.semantic.background.warning,
        color: theme.palette.semantic.text.warning,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.semantic.background.warning,
          fill: theme.palette.primary.dark,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },
      '&.MuiChip-colorSuccess': {
        backgroundColor: theme.palette.semantic.background.positive,
        color: theme.palette.semantic.text.positive,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.semantic.background.positive,
          fill: theme.palette.primary.dark,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },
      '&.MuiChip-colorInfo': {
        backgroundColor: theme.palette.semantic.background.highlight,
        color: theme.palette.semantic.text.highlight,
        '& .MuiChip-deleteIconSmall': {
          color: theme.palette.semantic.background.highlight,
          fill: theme.palette.primary.dark,
          padding: '4px',
          '&:hover': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}14`, // opacity 8%
          },
          '&:active': {
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.dark}1F`, // opacity 12%
          },
        },
        '&.Mui-disabled': {
          opacity: 1,
          '& .MuiChip-deleteIconSmall': {
            fill: theme.palette.text.disabled,
          },
        },
      },

      '&.MuiChip-sizeSmall': {
        fontSize: 10,
        height: 20,
        paddingInline: 4,
        '& .MuiChip-labelSmall': {
          paddingLeft: '2px',
          paddingRight: '2px',
        },
        '& .MuiChip-iconSmall': {
          width: 12,
          height: 12,
          marginLeft: '0px',
          marginRight: '0px',
        },
        '& .MuiChip-deleteIconSmall': {
          marginRight: '0px',
          marginLeft: '4px',
          width: 10,
          height: 10,
        },
      },
      '&.MuiChip-sizeMedium': {
        paddingInline: '8px',
        fontSize: '11px',
        height: '22px',
        '& .MuiChip-labelMedium': {
          paddingLeft: '0px',
          paddingRight: '0px',
        },
        '& .MuiChip-iconMedium': {
          width: 12,
          height: 12,
          marginLeft: '0px',
          marginRight: '4px',
        },
        '& .MuiChip-deleteIconSmall': {
          marginRight: '0px',
          marginLeft: '4px',
          width: 10,
          height: 10,
        },
      },
      '&.MuiChip-sizeSmallCompact': {
        fontSize: '10px !important',
        padding: '3px !important',
        height: '20px !important',
      },

      '&.MuiChip-variantBadgePillDefault': {
        borderRadius: '9999px',
        color: '#FFFFFF',
        fontWeight: '700',
        height: '20px',
        minWidth: '20px',
        fontSize: '10px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingLeft: '0px',
        paddingRight: '0px',
        whiteSpace: 'nowrap',
        '& .MuiChip-label': {
          padding: '2px 8px',
          paddingInline: '8px',
        },
      },
      '&.MuiChip-variantBadgePillNotification': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'unset',
        verticalAlign: 'middle',
        boxSizing: 'border-box',
        color: 'rgb(255, 255, 255)',
        fontWeight: '700',
        height: '18px',
        width: '18px',
        minWidth: '18px',
        fontSize: '10px',
        backgroundColor: 'rgb(231, 76, 60)',
        whiteSpace: 'nowrap',
        transition:
          'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        outline: '0px',
        textDecoration: 'none',
        paddingInline: '0px',
        paddingBlock: '0px',
        borderRadius: '9999px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgb(231, 76, 60)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '& .MuiChip-label': {
          padding: '0px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    }),
  },
});
export default getMuiChip;
